/**
 * SEOHead.jsx
 * ─────────────────────────────────────────────────────────────────
 * Reusable SEO component for Fiieve.
 * Drop this into EVERY page and pass the correct props.
 *
 * USAGE EXAMPLE:
 *   import SEOHead from "../components/SEOHead";
 *
 *   <SEOHead
 *     title="Web Development Company UK | Fiieve"
 *     description="Fiieve builds high-performance websites, apps & AI automation systems for UK businesses."
 *     canonical="https://fiieve.com/"
 *     ogImage="https://fiieve.com/Images/og-home.jpg"
 *   />
 *
 * REQUIRES: npm install react-helmet-async
 * WRAP your app root in: <HelmetProvider> ... </HelmetProvider>
 * ─────────────────────────────────────────────────────────────────
 */

import React from "react";
import { Helmet } from "react-helmet-async";

const SEOHead = ({
  // Page title — keep under 60 characters
  title = "Web Development & AI Automation Company UK | Fiieve",

  // Meta description — keep under 160 characters
  description = "Fiieve builds high-performance websites, apps & AI automation systems for UK businesses. Expert web developers & AI engineers. Get a free quote today.",

  // Canonical URL — prevents duplicate content penalties
  canonical = "https://fiieve.com/",

  // Open Graph image — shown when shared on social media (1200x630px recommended)
  ogImage = "https://fiieve.com/Images/og-default.jpg",

  // Page type — usually "website" for home, "article" for blogs
  ogType = "website",

  // Optional: extra schema markup (JSON-LD) — pass as a string
  schema = null,

  // Optional: noindex for pages you don't want Google to crawl (e.g. /portal, /privacy-policy)
  noIndex = false,
}) => {
  return (
    <Helmet>
      {/* ── Core Meta ── */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {!noIndex && <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />}

      {/* ── Open Graph (Facebook, LinkedIn, WhatsApp) ── */}
      <meta property="og:type"        content={ogType} />
      <meta property="og:title"       content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url"         content={canonical} />
      <meta property="og:image"       content={ogImage} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name"   content="Fiieve" />
      <meta property="og:locale"      content="en_GB" />

      {/* ── Twitter / X Card ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={ogImage} />
      <meta name="twitter:site"        content="@fiieve" />

      {/* ── JSON-LD Schema (optional, passed per page) ── */}
      {schema && (
        <script type="application/ld+json">{schema}</script>
      )}
    </Helmet>
  );
};

export default SEOHead;